import React from 'react';
import { motion } from 'framer-motion';
import { GraduationCap, Briefcase, Users2, Landmark, Shield, Crown, Layers } from 'lucide-react';
export function SolutionsPage() {
  const solutions = [{
    icon: <GraduationCap size={28} data-id="element-1668" />,
    title: 'For Education',
    color: 'bg-teal',
    link: '#education',
    description: 'SyNeural can support schools, universities, and learning systems by strengthening reasoning, emotional regulation, media literacy, self-direction, and long-term capability development. At early stages, the framework can help identify developmental bottlenecks and connect them to support pathways. In later stages, it can help students build stronger logic, planning ability, consequential thinking, and resilience against manipulation. This makes the platform relevant not only for academic performance, but for the broader development of readiness, responsibility, and civic capability.'
  }, {
    icon: <Briefcase size={28} data-id="element-1669" />,
    title: 'For Employers and Workforce Programs',
    color: 'bg-navy-light',
    link: '#employers',
    description: 'In workforce and organizational environments, SyNeural can help strengthen execution, communication, advancement readiness, decision quality, and leadership development. The platform can be used to identify where individuals or teams need stronger planning, reasoning, discipline, or stress tolerance, then connect those insights to practical pathways for growth. This makes it useful for talent development, internal mobility, performance support, and leadership pipelines.'
  }, {
    icon: <Users2 size={28} data-id="element-1670" />,
    title: 'For Civil Society',
    color: 'bg-green-soft',
    link: '#civil-society',
    description: 'Civil society organizations, nonprofits, mentorship networks, and community-based institutions can use SyNeural to strengthen misinformation resilience, local leadership, opportunity alignment, and community capability-building. The platform can help identify where support systems are weak, where growth potential is underdeveloped, and where communities need stronger pathways for participation, self-direction, and constructive engagement.'
  }, {
    icon: <Landmark size={28} data-id="element-1671" />,
    title: 'For Government and Public Institutions',
    color: 'bg-navy',
    link: '#government',
    description: 'SyNeural can support public institutions through workforce modernization, capability mapping, civic resilience planning, and leadership preparation. Municipal systems can use it to identify local bottlenecks such as weak youth pathways, low workforce readiness, or high misinformation vulnerability. Regional and national systems can use it to support education reform, public-sector development, ministry-level planning, and long-range stability initiatives. This makes the platform relevant not only as a development tool, but as strategic infrastructure for human capability and institutional performance.'
  }, {
    icon: <Crown size={28} data-id="element-1672" />,
    title: 'For Leadership Development',
    color: 'bg-teal',
    link: '#leadership',
    description: 'At higher levels of responsibility, SyNeural becomes a more rigorous framework for leadership readiness. The platform can support the evaluation and development of individuals moving into roles where poor judgment carries broad institutional or national consequences. It helps shift leadership development away from image, charisma, or status alone and toward consequence reasoning, stability, foresight, crisis performance, and institutional responsibility.'
  }];
  return <div className="min-h-screen bg-surface-bg" data-id="element-1673">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-1674">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-1675">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-1676">
            <span className="w-8 h-px bg-teal" data-id="element-1677"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-1678">
              Solutions
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-1679"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-1680">
            Solutions Across the Full Human and Institutional Spectrum
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-1681">
            SyNeural's platform architecture is designed for flexible deployment
            across education, workforce systems, civil society, public
            institutions, and leadership environments.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1682">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1683">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-1684">
            Through CJI and NCJI, the platform helps measure, strengthen, and
            develop judgment in ways that are relevant to each setting, from
            early capability formation to institutional readiness and
            executive-level responsibility.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1685">
            Rather than offering a one-size-fits-all model, SyNeural adapts its
            framework to the level of responsibility, complexity, and
            consequence involved. At the individual level, it supports growth,
            readiness, and opportunity alignment. At the institutional level, it
            supports capability-building, leadership development, and decision
            quality. At the public-system level, it supports resilience,
            modernization, and long-range planning.
          </p>
        </motion.div>
      </section>

      {/* Three-Level Framework */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-1686">
        <div className="max-w-5xl mx-auto" data-id="element-1687">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1688">
            <div className="bg-white p-8 rounded-lg border border-line" data-id="element-1689">
              <div className="w-12 h-12 rounded-lg bg-teal/10 flex items-center justify-center text-teal mb-6" data-id="element-1690">
                <Users2 size={24} data-id="element-1691" />
              </div>
              <h3 className="text-xl font-heading font-bold text-ink-dark mb-3" data-id="element-1692">
                Individual Level
              </h3>
              <p className="text-ink-muted leading-relaxed" data-id="element-1693">
                Growth, readiness, and opportunity alignment
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg border border-line" data-id="element-1694">
              <div className="w-12 h-12 rounded-lg bg-navy-light/10 flex items-center justify-center text-navy-light mb-6" data-id="element-1695">
                <Landmark size={24} data-id="element-1696" />
              </div>
              <h3 className="text-xl font-heading font-bold text-ink-dark mb-3" data-id="element-1697">
                Institutional Level
              </h3>
              <p className="text-ink-muted leading-relaxed" data-id="element-1698">
                Capability-building, leadership development, and decision
                quality
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg border border-line" data-id="element-1699">
              <div className="w-12 h-12 rounded-lg bg-navy/10 flex items-center justify-center text-navy mb-6" data-id="element-1700">
                <Layers size={24} data-id="element-1701" />
              </div>
              <h3 className="text-xl font-heading font-bold text-ink-dark mb-3" data-id="element-1702">
                Public-System Level
              </h3>
              <p className="text-ink-muted leading-relaxed" data-id="element-1703">
                Resilience, modernization, and long-range planning
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto" data-id="element-1704">
        <div className="space-y-20" data-id="element-1705">
          {solutions.map((solution, index) => <motion.div key={solution.title} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6,
          delay: index * 0.1
        }} className={`${index % 2 === 0 ? '' : 'lg:flex-row-reverse'} flex flex-col lg:flex-row gap-12 items-start`} data-id="element-1706">
              {/* Icon & Title Column */}
              <div className="lg:w-1/3 flex-shrink-0" data-id="element-1707">
                <div className={`w-16 h-16 rounded-xl ${solution.color} flex items-center justify-center text-white mb-6 shadow-lg`} data-id="element-1708">
                  {solution.icon}
                </div>
                <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1709">
                  {solution.title}
                </h2>
              </div>

              {/* Description Column */}
              <div className="lg:w-2/3" data-id="element-1710">
                <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-1711">
                  <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1712">
                    {solution.description}
                  </p>
                </div>
              </div>
            </motion.div>)}
        </div>
      </section>

      {/* Why It Matters */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-navy-deep text-white" data-id="element-1713">
        <div className="max-w-4xl mx-auto" data-id="element-1714">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1715">
            <div className="flex items-center gap-4 mb-8" data-id="element-1716">
              <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center" data-id="element-1717">
                <Layers size={24} data-id="element-1718" />
              </div>
              <h2 className="text-3xl font-heading font-bold" data-id="element-1719">
                Why the Solutions Model Matters
              </h2>
            </div>

            <p className="text-xl leading-relaxed opacity-90 mb-6" data-id="element-1720">
              The strength of the SyNeural platform lies in its ability to adapt
              one unified architecture across very different environments
              without losing coherence.
            </p>

            <p className="text-lg leading-relaxed opacity-80" data-id="element-1721">
              The same foundational logic applies throughout: identify
              capability, clarify bottlenecks, connect them to pathways, support
              development, and strengthen readiness over time. That makes the
              platform useful not just for measurement, but for real-world
              growth across the full human and institutional spectrum.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Visual Summary */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto" data-id="element-1722">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1723">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-12 text-center" data-id="element-1724">
            One Platform, Multiple Applications
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-id="element-1725">
            {[{
            icon: <GraduationCap size={20} data-id="element-1726" />,
            label: 'Education',
            link: '#education'
          }, {
            icon: <Briefcase size={20} data-id="element-1727" />,
            label: 'Employers',
            link: '#employers'
          }, {
            icon: <Users2 size={20} data-id="element-1728" />,
            label: 'Civil Society',
            link: '#civil-society'
          }, {
            icon: <Landmark size={20} data-id="element-1729" />,
            label: 'Government',
            link: '#government'
          }, {
            icon: <Crown size={20} data-id="element-1730" />,
            label: 'Leadership',
            link: '#leadership'
          }].map((item, index) => <motion.a key={item.label} href={item.link} initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.05
          }} className="bg-surface-muted p-6 rounded-lg border border-line text-center hover:border-navy hover:shadow-md transition-all" data-id="element-1731">
                <div className="w-10 h-10 rounded-md bg-white flex items-center justify-center text-navy mx-auto mb-3 border border-line" data-id="element-1732">
                  {item.icon}
                </div>
                <div className="text-sm font-medium text-ink-dark" data-id="element-1733">
                  {item.label}
                </div>
              </motion.a>)}
          </div>
        </motion.div>
      </section>
    </div>;
}